import type { PageDocument } from '../../prismicio-types';

// Tag convention for virtual parent segments. A page tagged
// `virtual-parent:about` renders under /about/<uid> even though no Prismic
// doc exists for `about`. This is content-editable — no code change needed
// for new virtual parents. Only the FIRST matching tag is honored.
export const VIRTUAL_PARENT_TAG_PREFIX = 'virtual-parent:';

const segmentCount = (url: string | null | undefined): number =>
  url ? url.split('/').filter(Boolean).length : 0;

// Returns the virtual-parent URL segment from the first matching tag, or
// null if none is set.
export const getVirtualParentSegment = (
  tags: readonly string[] | undefined | null,
): string | null => {
  if (!tags?.length) {
    return null;
  }
  for (const tag of tags) {
    if (typeof tag !== 'string') {
      continue;
    }
    if (!tag.startsWith(VIRTUAL_PARENT_TAG_PREFIX)) {
      continue;
    }
    const segment = tag.slice(VIRTUAL_PARENT_TAG_PREFIX.length).trim();
    if (segment) {
      return segment;
    }
  }
  return null;
};

// Canonical URL for a `page` doc. Priority — never inverts:
//   1. `parent_page` link resolved by Prismic → use `page.url` (has 2+ segments)
//   2. `virtual-parent:<segment>` tag       → /<segment>/<uid>
//   3. Fallback                              → `page.url` or /<uid>
//
// Step 1 uses `page.url` (not a manual reconstruction from the link field)
// because Prismic's route resolver processes the response server-side and
// populates `page.url` correctly whenever the parent doc is accessible via
// the current ref. Reading `page.data.parent_page.uid` in code fails in edge
// cases where Prismic could still resolve the URL — those edge cases are the
// exact bug that was causing /parent/child to collapse to /child.
export const resolvePageUrl = (
  page: Pick<PageDocument, 'uid' | 'url' | 'tags'>,
): string | null => {
  if (!page.uid) {
    return null;
  }
  if (page.uid === 'home') {
    return '/';
  }

  const url = page.url ?? null;

  // (1) Real parent — Prismic gave us the nested URL. Trust it.
  if (url && segmentCount(url) >= 2) {
    return url;
  }

  // (2) Virtual parent via tag — only when no real parent nesting is present.
  const virtual = getVirtualParentSegment(page.tags);
  if (virtual) {
    return `/${virtual}/${page.uid}`;
  }

  // (3) Plain root page.
  return url ?? `/${page.uid}`;
};
